import { useEffect, useState, useRef } from "react";
import LoadingSection from "../LoadingSection";
import Error from "../Error";
import { useDispatch, useSelector } from 'react-redux';
import Alert from '../Alert';
import { selectAccountData } from "../../redux/main/accountSlice";
import Loader from "../Loader";
import SystemServicePool from "./SystemServicesPool";
import { CustomerSelect } from "../CustomerSelect";
import { toast } from 'react-toastify';
import {
    getSystemDetails,
    selectSystemDetailsData,
    selectSystemDetailsError,
    selectSystemDetailsIsLoading,
    resetSystemDetails
} from "../../redux/systems/systemDetailsSlice";
import { selectRightModalOldState, selectRightModalData, setGoingToClose, setHasToRefresh, setSwap, setModal, swapModal, selectRightModalIsRefreshNow, selectRightModalLastClosedData, resetRefreshNow } from "../../redux/rightModalSlice";
import { useColorPalette } from '../../hooks/useColorPalette';
import DynamicIcon from "../DynamicIcon";

export default function AddEditSystem({ systemId = null, onSaveComplete = null, preselectedCustomer = null }) {
    // Chiamata iniziale per ricevere i dettagli del system
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    const palette = useColorPalette(); // Hook per la palette colori dinamica
    
    // Selettori Redux per la modale - DEVONO essere dichiarati PRIMA degli useEffect che li usano
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    const rightModalLastClosedData = useSelector(selectRightModalLastClosedData);
    const rightModalData = useSelector(selectRightModalData);
    
    const [systemDataInput, setSystemDataInput] = useState({
        accountId: null
    });
    
    // Uso un ref per tracciare se è già stato inizializzato
    const isInitialized = useRef(false);
    
    // Effetto per resettare lo store Redux quando si apre l'offcanvas in modalità nuovo impianto
    useEffect(() => {
        if (!systemId) {
            // Reset dello store Redux per evitare di mostrare dati di un impianto precedente
            dispatch(resetSystemDetails());
        }
        
        // Cleanup: reset flag inizializzazione quando il componente viene smontato
        return () => {
            isInitialized.current = false;
        };
    }, [dispatch, systemId]);
    
    // Imposto accountId come dato iniziale e resetto i campi quando si apre l'offcanvas per un nuovo impianto
    
    useEffect(() => {
        console.log('🏁 useEffect inizializzazione trigger:', {
            hasAccountData: !!accountData,
            isInitialized: isInitialized.current,
            systemId,
            hasSavedData: !!rightModalData?.savedSystemDataInput,
            currentCustomerId: systemDataInput.customerId
        });
        
        if (accountData && !isInitialized.current) {
            // Se abbiamo dati salvati da ripristinare, NON resettare il form
            if (rightModalData && rightModalData.savedSystemDataInput) {
                console.log('⏭️ Skip inizializzazione: ci sono dati da ripristinare');
                isInitialized.current = true;
                return;
            }
            
            // Se systemId è null, significa che stiamo creando un nuovo impianto
            if (!systemId) {
                setSystemDataInput({
                    accountId: accountData._id,
                    title: '',
                    typesList: [],
                    name: ''
                });
                console.log('✨ Inizializzazione form per nuovo impianto');
            } else {
                // In modalità edit, manteniamo accountId
                setSystemDataInput(prev => ({
                    ...prev,
                    accountId: accountData._id
                }));
                console.log('✨ Inizializzazione form per edit impianto');
            }
            isInitialized.current = true;
        } else {
            console.log('⏭️ Skip inizializzazione');
        }
    }, [accountData, systemId, rightModalData]);
    
    // Assicura che accountId sia sempre presente quando manca
    useEffect(() => {
        if (accountData && !systemDataInput.accountId && isInitialized.current) {
            setSystemDataInput(prevState => ({
                ...prevState,
                accountId: accountData._id
            }));
        }
    }, [accountData, systemDataInput.accountId]);
    // Imposto i dati del cliente se in modalità edit
    const systemDetailsData = useSelector(selectSystemDetailsData);
    const systemDetailsIsLoading = useSelector(selectSystemDetailsIsLoading);
    const systemDetailsError = useSelector(selectSystemDetailsError);
    useEffect(() => {
        if (systemId) dispatch(getSystemDetails({ systemId, token }));
    }, [systemId])
    // Ricevuti i dati system da Redux imposto il systemDataInput per la modifica
    useEffect(() => {
        if (systemDetailsData) {
            setSystemDataInput((prevState) => ({
                ...prevState,
                ...systemDetailsData
            }))
        }
    }, [systemDetailsData])

    // Funzione generica per aggiornare il systemDataInput
    const handleSystemDataInput = (event) => {
        const { id, value } = event.target;
        setSystemDataInput((prevState) => ({
            ...prevState,
            [id]: value
        }))
    }
    // Funzione per da passare alla Pool per aggiungere/modificare un service
    // Gestisce l'array typesList
    const addEditItem = (newObject, index) => {
        setSystemDataInput((prevState) => {
            // Se l'array è inizialmente vuoto, creo un nuovo array
            // Contenente il nuovo oggetto
            if (!prevState.typesList) {
                return {
                    ...prevState,
                    typesList: [newObject]
                }
            };
            // Se è fornito un indice, sostituisco l'elemento a quell'indice
            if (index !== undefined && index !== null) {
                const updatedArray = [...prevState.typesList];
                updatedArray[index] = newObject;
                return {
                    ...prevState,
                    typesList: updatedArray
                }
            };
            // Altrimenti aggiungo il nuovo oggetto alla fine dell'array
            return {
                ...prevState,
                typesList: [...prevState.typesList, newObject]
            }
        })
    }
    // Funzione da passare alla Pool per eliminare un elemento
    const deleteItem = (index) => {
        const updatedArray = [...systemDataInput.typesList];
        updatedArray.splice(index, 1);
        setSystemDataInput((prevState) => ({
            ...prevState,
            typesList: updatedArray
        }))
    }

    const [sendFetchStatus, setSendFetchStatus] = useState('i');
    const [sendError, setSendError] = useState('');
    const [validationErrors, setValidationErrors] = useState([]);
    const [showValidation, setShowValidation] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const sendSystemData = async () => {
        // Reset degli stati di validazione precedenti
        setValidationErrors([]);
        setShowValidation(false);
        
        // Validazione dati obbligatori
        const errors = [];
        
        // 1. Controllo nome impianto (usiamo title o name se disponibili)
        if ((!systemDataInput.title || systemDataInput.title.trim() === '') && 
            (!systemDataInput.name || systemDataInput.name.trim() === '')) {
            errors.push('È necessario specificare un nome per l\'impianto');
            console.log('Validazione nome impianto fallita:', systemDataInput);
        }
        
        // 2. Controllo cliente selezionato
        if (!systemDataInput.customerId) {
            errors.push('È necessario selezionare un cliente');
        }
        
        // 3. Controllo indirizzo selezionato (usiamo customerAddressId o addressId se disponibili)
        if (!systemDataInput.customerAddressId && !systemDataInput.addressId) {
            errors.push('È necessario selezionare un indirizzo');
            console.log('Validazione indirizzo fallita:', systemDataInput);
        }
        
        // 4. Controllo almeno un tipo impianto
        if (!systemDataInput.typesList || systemDataInput.typesList.length === 0) {
            errors.push('È necessario aggiungere almeno un tipo di impianto');
        }
        
        // Se ci sono errori di validazione, mostro l'alert e interrompo
        if (errors.length > 0) {
            setValidationErrors(errors);
            setShowValidation(true);
            setSendFetchStatus('i'); // Mantengo lo stato iniziale
            return;
        }
        
        // Procedo con il salvataggio
        setSendFetchStatus('l');
        
        // Prepariamo i dati per il salvataggio
        const systemToSave = {...systemDataInput};
        
        // Assicuriamoci che ci sia un id dell'indirizzo valido
        if (systemToSave.customerAddressId && !systemToSave.customerAddressLabel && availableAddresses) {
            // Cerchiamo l'indirizzo selezionato nell'array degli indirizzi disponibili
            const selectedAddress = availableAddresses.find(addr => addr._id === systemToSave.customerAddressId);
            if (selectedAddress) {
                systemToSave.customerAddressLabel = selectedAddress.addressLabel || 
                    `${selectedAddress.address || ''}${selectedAddress.city ? `, ${selectedAddress.city}` : ''}`;
            }
        }
        
        // Debug per verificare i dati prima dell'invio
        console.log('Dati impianto prima dell\'invio:', systemToSave);
        
        const url = `${import.meta.env.VITE_API_BASE_URL}/systems`;
        const bodyJson = systemId ? { system: systemToSave } : { system: systemToSave };
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = {
            method: systemId ? 'PATCH' : 'POST',
            headers,
            body: JSON.stringify(bodyJson)
        }
        
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                const errorData = await response.json();
                throw errorData;
            } else {
                const resultData = await response.json();
                setSendFetchStatus('s');
                setShowValidation(false);
                
                // Mostra messaggio di successo se è un nuovo impianto
                if (!systemId) {
                    // Imposta il messaggio di successo colorato
                    setSuccessMessage('Impianto creato con successo');
                    
                    console.log('✅ Impianto creato, dati ricevuti dal backend:', resultData);
                    console.log('✅ resultData.system:', resultData.system);
                    console.log('✅ Tipo di resultData:', typeof resultData);
                    
                    // Chiudi l'offcanvas e ricarica la lista dopo un breve ritardo
                    setTimeout(() => {
                        const systemData = resultData.system || resultData;
                        console.log('💾 Impostazione lastClosedData con systemData:', systemData);

                        dispatch(setHasToRefresh());

                        if (rightModalOldState && rightModalOldState.oldMode) {
                            // Swap diretto (es. Lead → Sistema → salva)
                            dispatch(swapModal({
                                createdSystem: systemData
                            }));
                        } else if (savedParentOldStateRef.current && savedParentOldStateRef.current.oldMode) {
                            // Swap annidato (es. Lead → Sistema → Indirizzo → Sistema → salva)
                            // L'oldState originale e' stato perso durante lo swap con l'indirizzo
                            const parentState = savedParentOldStateRef.current;
                            console.log('🔄 Ripristino parent da swap annidato:', parentState.oldMode);
                            dispatch(setModal({
                                mode: parentState.oldMode,
                                id: parentState.oldId,
                                data: {
                                    ...parentState.oldData,
                                    createdSystem: systemData
                                }
                            }));
                            savedParentOldStateRef.current = null;
                        } else {
                            // Salvataggio normale (senza swap)
                            dispatch(setModal({
                                mode: 'add',
                                id: null,
                                data: {
                                    systemData: systemData,
                                    systemId: systemData._id || systemData.id || systemData,
                                    systemLabel: systemData.title || systemData.name || 'Nuovo impianto'
                                }
                            }));
                            dispatch(setGoingToClose());
                        }

                        // Invochiamo la callback se presente
                        if (onSaveComplete && typeof onSaveComplete === 'function') {
                            onSaveComplete();
                        }
                    }, 2000); // Attende 2 secondi prima di chiudere
                } else {
                    // Per la modifica, comportamento normale
                    dispatch(setHasToRefresh());
                    
                    // Invochiamo la callback se presente
                    if (onSaveComplete && typeof onSaveComplete === 'function') {
                        onSaveComplete();
                    }
                }
            }
        } catch (error) {
            console.error(error.message || "Errore da parte del server.");
            setSendFetchStatus('f');
        }

    }

    // Gestisco lo stato della React-Select e la sincronizzazione con systemDataInput
    const [selectedOption, setSelectedOption] = useState(null);
    const [availableAddresses, setAvailableAddresses] = useState(null);
    const systemServicePoolRef = useRef(null);

    // Ref per tracciare se abbiamo già ripristinato lo stato
    const hasRestoredState = useRef(false);
    // Ref per salvare l'oldState del parent (es. Lead) quando si fa swap annidato (Sistema → Indirizzo)
    const savedParentOldStateRef = useRef(null);
    
    // Effetto per ripristinare lo stato salvato quando si torna dalla modale AddEditAddress
    useEffect(() => {
        console.log('🔍 useEffect ripristino stato trigger:', {
            hasRightModalData: !!rightModalData,
            hasSavedSystemDataInput: !!rightModalData?.savedSystemDataInput,
            hasRestoredState: hasRestoredState.current
        });
        
        // Ripristina lo stato solo se abbiamo dati salvati e non l'abbiamo già fatto
        if (rightModalData && rightModalData.savedSystemDataInput && !hasRestoredState.current) {
            console.log('🔄 Ripristino stato salvato:', rightModalData);
            
            // Ripristina il cliente selezionato
            if (rightModalData.preselectedCustomer) {
                const customerOption = {
                    value: rightModalData.preselectedCustomer.id || rightModalData.preselectedCustomer.value,
                    label: rightModalData.preselectedCustomer.label,
                    data: {
                        _id: rightModalData.preselectedCustomer.id || rightModalData.preselectedCustomer.value,
                        customerId: rightModalData.preselectedCustomer.id || rightModalData.preselectedCustomer.value,
                        customerLabel: rightModalData.preselectedCustomer.label,
                        company: rightModalData.preselectedCustomer.label
                    }
                };
                setSelectedOption(customerOption);
                console.log('✅ Cliente ripristinato:', customerOption);
            }
            
            // Ripristina gli indirizzi disponibili
            if (rightModalData.savedAvailableAddresses) {
                setAvailableAddresses(rightModalData.savedAvailableAddresses);
                console.log('✅ Indirizzi ripristinati:', rightModalData.savedAvailableAddresses.length);
            }
            
            // Ripristina systemDataInput
            setSystemDataInput(prevState => ({
                ...prevState,
                ...rightModalData.savedSystemDataInput
            }));
            console.log('✅ SystemDataInput ripristinato');
            
            // Salva l'oldState del parent (es. Lead) se presente, per il salvataggio successivo
            if (rightModalData.savedParentOldState && rightModalData.savedParentOldState.oldMode) {
                savedParentOldStateRef.current = rightModalData.savedParentOldState;
                console.log('✅ Parent oldState salvato per swap annidato:', rightModalData.savedParentOldState.oldMode);
            }

            // Marca come ripristinato
            hasRestoredState.current = true;
        }
    }, [rightModalData]);
    
    // Reset del flag quando il componente viene smontato
    useEffect(() => {
        return () => {
            hasRestoredState.current = false;
        };
    }, []);
    
    // Effetto per gestire la creazione di un nuovo indirizzo
    useEffect(() => {
        console.log('🔍 useEffect nuovo indirizzo trigger:', {
            rightModalIsRefreshNow,
            hasAddressData: !!rightModalLastClosedData?.addressData,
            systemDataInputCustomerId: systemDataInput?.customerId
        });
        
        if (rightModalIsRefreshNow && rightModalLastClosedData && rightModalLastClosedData.addressData) {
            console.log('✅ Nuovo indirizzo creato:', rightModalLastClosedData.addressData);
            console.log('📋 Stato attuale systemDataInput:', systemDataInput);
            
            // Ricarica gli indirizzi del cliente
            if (systemDataInput.customerId) {
                const customerId = systemDataInput.customerId;
                const apiUrl = `${import.meta.env.VITE_API_BASE_URL || ''}/customers?customerId=${customerId}`;
                
                console.log('🔄 Ricaricamento indirizzi per cliente:', customerId);
                
                fetch(apiUrl, {
                    method: 'GET',
                    headers: { 'auth-token': token }
                })
                .then(response => response.json())
                .then(data => {
                    console.log('✅ Dati cliente ricaricati:', data);
                    if (data.customer && data.customer.addresses) {
                        console.log('📍 Indirizzi totali:', data.customer.addresses.length);
                        setAvailableAddresses(data.customer.addresses);
                        
                        // Seleziona automaticamente il nuovo indirizzo
                        const newAddress = rightModalLastClosedData.addressData;
                        console.log('🎯 Tentativo selezione indirizzo con ID:', newAddress._id);
                        
                        if (newAddress._id) {
                            // Cerco l'indirizzo completo nella lista ricaricata
                            const foundAddress = data.customer.addresses.find(addr => addr._id === newAddress._id);
                            if (foundAddress) {
                                console.log('✅ Indirizzo trovato nella lista:', foundAddress);
                                handleSelectAddress(foundAddress);
                                console.log('✅ Indirizzo selezionato automaticamente');
                                toast.success(`Indirizzo "${foundAddress.alias || 'Nuovo indirizzo'}" selezionato`);
                            } else {
                                console.warn('⚠️ Indirizzo non trovato nella lista ricaricata');
                                // Prova comunque a selezionare quello passato
                                handleSelectAddress(newAddress);
                                toast.info('Indirizzo aggiunto alla lista');
                            }
                        }
                    }
                })
                .catch(error => {
                    console.error('❌ Errore durante il ricaricamento degli indirizzi:', error);
                });
            } else {
                console.warn('⚠️ customerId non presente in systemDataInput:', systemDataInput);
                console.warn('⚠️ Aspetto che systemDataInput venga ripristinato...');
                // Non resettare il flag, lascialo attivo per quando systemDataInput sarà ripristinato
                return;
            }
            
            // Reset del flag DOPO aver completato il refresh
            setTimeout(() => {
                dispatch(resetRefreshNow());
                console.log('🔄 Flag refresh resettato');
            }, 100);
        }
    }, [rightModalIsRefreshNow, rightModalLastClosedData, systemDataInput.customerId, token, dispatch]);
    
    // Effetto per impostare il cliente preselezionato quando si crea un nuovo impianto
    useEffect(() => {
        console.log('🔍 useEffect preselectedCustomer trigger:', {
            preselectedCustomer,
            systemId,
            selectedOption,
            customerId: systemDataInput.customerId
        });
        
        if (preselectedCustomer && !systemId && !selectedOption) {
            console.log('✅ Impostazione cliente preselezionato:', preselectedCustomer);
            const customerOption = {
                value: preselectedCustomer.id || preselectedCustomer.value,
                label: preselectedCustomer.label,
                data: {
                    _id: preselectedCustomer.id || preselectedCustomer.value,
                    customerId: preselectedCustomer.id || preselectedCustomer.value,
                    customerLabel: preselectedCustomer.label,
                    company: preselectedCustomer.label
                }
            };
            setSelectedOption(customerOption);
            console.log('✅ Cliente preselezionato impostato:', customerOption);
        } else {
            console.log('⏭️ Skip preselezione:', {
                hasPreselected: !!preselectedCustomer,
                isNewSystem: !systemId,
                alreadySelected: !!selectedOption
            });
        }
    }, [preselectedCustomer, systemId]);
    
    // Effetto quando systemDataInput cambia (es. caricamento iniziale)
    useEffect(() => {
        // Se systemDataInput contiene dati cliente ma selectedOption è null,
        // crea un oggetto Option basato sui dati esistenti
        if (
            systemDataInput?.customerId &&
            systemDataInput?.customerLabel &&
            !selectedOption
        ) {
            const newOption = {
                value: systemDataInput.customerId,
                label: systemDataInput.customerLabel,
                data: {
                    customerId: systemDataInput.customerId || '',
                    customerLabel: systemDataInput.customerLabel || '',
                    customerAddresses: systemDataInput.customerAddresses || [],
                }
            };

            setSelectedOption(newOption);
        }
    }, [systemDataInput, selectedOption]);
    // Effetto quando selectedOption cambia (mantenere quello esistente)
    useEffect(() => {
        if (selectedOption) {
            console.log('Cliente selezionato:', selectedOption);
            
            // Determina l'ID del cliente in base alla struttura dei dati
            const customerId = selectedOption.data._id || selectedOption.data.customerId || selectedOption.value;
            const customerLabel = selectedOption.data.company || selectedOption.data.text || selectedOption.data.customerLabel || selectedOption.label;
            
            setSystemDataInput((prevState) => ({
                ...prevState,
                customerId,
                customerLabel,
            }));
            
            // Dobbiamo recuperare gli indirizzi dal backend
            const token = localStorage.getItem('installamelo-auth-token');
            if (token && customerId) {
                console.log('Recupero indirizzi per il cliente:', customerId);
                fetch(`${import.meta.env.VITE_API_BASE_URL}/customers?customerId=${customerId}`, {
                    method: 'GET',
                    headers: {
                        'auth-token': token,
                        'Content-Type': 'application/json'
                    }
                })
                .then(response => response.json())
                .then(data => {
                    console.log('Dati cliente completi:', data);
                    // Verifica dove si trovano gli indirizzi
                    if (data.customer && data.customer.addresses && data.customer.addresses.length > 0) {
                        setAvailableAddresses(data.customer.addresses);
                    } else if (data.addresses && data.addresses.length > 0) {
                        setAvailableAddresses(data.addresses);
                    } else if (selectedOption.data.addresses && selectedOption.data.addresses.length > 0) {
                        setAvailableAddresses(selectedOption.data.addresses);
                    } else {
                        console.warn('Nessun indirizzo trovato per il cliente');
                        setAvailableAddresses([]);
                    }
                })
                .catch(error => {
                    console.error('Errore durante il recupero degli indirizzi:', error);
                    setAvailableAddresses([]);
                });
            }
        } else {
            // Gestione quando selectedOption diventa null (rimozione cliente)
            setSystemDataInput(prevState => {
                const { customerId, customerLabel, customerAddressId, customerAddressLabel, ...rest } = prevState;
                return rest;
            });
            setAvailableAddresses(null);
        }
    }, [selectedOption]);
    // Alla selezione di un indirizzo, aggiorno l'oggetto systemDataInput
    const handleSelectAddress = (data) => {
        // Forza un re-render per aggiornare l'UI
        setSystemDataInput((prevState) => ({
            ...prevState,
            customerAddressId: data._id,
            customerAddressLabel: data.addressLabel,
            address: data.address, // Aggiungiamo questa proprietà per il confronto visivo
            geoData: {
                type: data.geoData.type,
                coordinates: data.geoData.coordinates,
                distanceKm: data.geoData?.distanceKm || null,
                distanceTime: data.geoData?.distanceTime || null
            }
        }))
    }
    // Gestisco il tasto Torna indietro in caso di provenienza da un'altra modale
    const rightModalOldState = useSelector(selectRightModalOldState);
    return (
        <div className="w-full p-8" style={{ color: palette.textColor || '#111827' }}>
            {
                systemDetailsIsLoading === true && <LoadingSection isFixed={false} />
            }
            {
                systemDetailsError && <Error message={systemDetailsError} />
            }
            {/* Messaggio di successo colorato */}
            {successMessage && (
                <Alert 
                    type="success" 
                    message={successMessage} 
                    autoCloseMs={2000} 
                    dismissible={false}
                />
            )}
            {
                ((systemId && systemDetailsData) || !systemId) &&
                <div className={`
                    flex
                    flex-col
                    gap-4
                    mt-8
                    border-2
                    rounded-lg
                    p-4
                    relative
                `}>

                    <h1>{systemId ? 'Modifica Impianto' : 'Nuovo Impianto'}</h1>
                    {systemId && <div className="light-text mt-[-24px]" style={{ color: palette.textMuted || '#6b7280' }}>Id impianto: {systemId}</div>}
                    {/* INFORMAZIONI */}
                    <h2>Informazioni</h2>
                    <div className="flex flex-col gap-4">
                        {/* Titolo */}
                        <div className="input-box w-full">
                            <label htmlFor="title">Nome Impianto</label>
                            <input
                                type="text"
                                id="title"
                                value={systemDataInput?.title || ""}
                                onChange={handleSystemDataInput}
                                className="w-full"
                            />
                        </div>
                        {/* Cliente */}
                        <div className="input-box">
                            <label htmlFor="customerList">{systemId ? 'Cliente' : 'Seleziona cliente'}</label>
                            {/* La scelta del cliente compare soltanto alla creazione, e solo se non e' gia' preselezionato */}
                            {
                                !systemId && !preselectedCustomer && !rightModalData?.preselectedCustomer &&
                                <div className="w-full">
                                    <CustomerSelect
                                        selectedOption={selectedOption}
                                        setSelectedOption={setSelectedOption}
                                        showSelectedLabel={false}
                                    />
                                </div>
                            }
                            {!systemId && (preselectedCustomer || rightModalData?.preselectedCustomer) && selectedOption && (
                                <div className="p-2 bg-gray-100 rounded-md text-sm font-medium">
                                    {selectedOption.label}
                                </div>
                            )}
                            {systemDataInput?.customerId && availableAddresses && availableAddresses.length > 0 &&
                                <div className="p-3 rounded border mt-3" style={{
                                    backgroundColor: palette.formInputBg || '#f9fafb',
                                    borderColor: palette.formBorderColor || '#d1d5db',
                                    color: palette.formInputText || '#111827'
                                }}>
                                    <div>
                                        {/* Box cliente con intestazione */}
                                        <div className="border rounded-lg overflow-hidden" style={{
                                            borderColor: palette.formBorderColor || '#d1d5db',
                                        }}>
                                            {/* Intestazione */}
                                            <div className="px-3 py-2 font-medium" style={{
                                                backgroundColor: palette.tableHeaderBg || '#f3f4f6',
                                                color: palette.tableHeaderText || '#111827'
                                            }}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <DynamicIcon iconClass="fa-solid fa-map-marker-alt" style={{ color: palette.iconColor || palette.textColor }} />
                                                        Seleziona indirizzo
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            console.log('💾 Apertura modale indirizzo con swap e salvataggio stato:', {
                                                                selectedOption,
                                                                systemDataInput,
                                                                availableAddresses: availableAddresses?.length
                                                            });
                                                            
                                                            // Apri la modale per aggiungere indirizzo
                                                            // setSwap con isOld:true e dataToSave salverà lo stato corrente
                                                            dispatch(setSwap({
                                                                mode: 'add_address',
                                                                data: {
                                                                    customerId: systemDataInput.customerId,
                                                                    customerData: {
                                                                        label: systemDataInput.customerLabel,
                                                                        customerLabel: systemDataInput.customerLabel
                                                                    }
                                                                },
                                                                percentuageWidth: 50,
                                                                isOld: true,
                                                                // Salva lo stato corrente del form da ripristinare quando si torna
                                                                dataToSave: {
                                                                    preselectedCustomer: selectedOption ? {
                                                                        id: selectedOption.value,
                                                                        label: selectedOption.label,
                                                                        value: selectedOption.value
                                                                    } : null,
                                                                    savedAvailableAddresses: availableAddresses,
                                                                    savedSystemDataInput: systemDataInput,
                                                                    savedParentOldState: rightModalOldState
                                                                }
                                                            }));
                                                        }}
                                                        className="flex items-center justify-center w-7 h-7 rounded transition-colors"
                                                        style={{
                                                            backgroundColor: palette.primary,
                                                            color: palette.textOnPrimary || '#ffffff'
                                                        }}
                                                        title="Aggiungi nuovo indirizzo"
                                                    >
                                                        <i className="fa-solid fa-plus text-xs"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            {/* Lista indirizzi */}
                                            <div className="divide-y" style={{ borderColor: palette.formBorderColor || '#d1d5db' }}>
                                                {
                                                    availableAddresses.map((address, index) => {
                                                        const isEven = index % 2 === 0;
                                                        const isSelected = systemDataInput.customerAddressId === address._id;
                                                        const rowBgColor = isSelected 
                                                            ? (palette.highlightBg || `${palette.primary}22` || '#e6f7ff') // Colore primario con opacità
                                                            : isEven ? palette.tableRowLight : palette.tableRowDark;
                                                        const rowTextColor = isSelected
                                                            ? (palette.primary || palette.textOnHighlight || '#0078d4')
                                                            : isEven ? palette.textOnTableRowLight : palette.textOnTableRowDark;
                                                        const borderStyle = isSelected
                                                            ? `2px solid ${palette.primary || palette.borderColorAccent || '#0078d4'}`
                                                            : 'none';
                                                            
                                                        return (
                                                            <div 
                                                                key={index} 
                                                                className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:opacity-90 transition-all"
                                                                style={{
                                                                    backgroundColor: rowBgColor,
                                                                    color: rowTextColor,
                                                                    border: borderStyle,
                                                                    borderRadius: isSelected ? '0.25rem' : '0'
                                                                }}
                                                                onClick={() => handleSelectAddress(address)}
                                                            >
                                                                <div className="flex-shrink-0">
                                                                    <DynamicIcon 
                                                                        iconClass="fa-solid fa-check-circle" 
                                                                        style={{ 
                                                                            color: isSelected ? (palette.primary || palette.success || '#22c55e') : (palette.iconColor || '#333333'),
                                                                            fontSize: '1.25rem',
                                                                            opacity: isSelected ? 1 : 0.5
                                                                        }} 
                                                                    />
                                                                </div>
                                                                
                                                                <div className="flex flex-col flex-grow">
                                                                    <span className="font-medium">
                                                                        {address.addressLabel || address.address || address.street || 'Indirizzo'}
                                                                    </span>
                                                                    {(address.city || address.cap || address.province) && (
                                                                        <span className="text-sm" style={{ color: isSelected ? rowTextColor : palette.textMuted || '#6b7280' }}>
                                                                            {address.city || ''}
                                                                            {address.cap ? (address.city ? ` (${address.cap})` : `${address.cap}`) : ''}
                                                                            {address.province ? (address.city || address.cap ? `, ${address.province}` : address.province) : ''}
                                                                        </span>
                                                                    )}
                                                                    
                                                                    {/* Visualizzazione distanza e tempo se disponibili */}
                                                                    {(address.geoData?.distanceKm || address.geoData?.distanceTime) && (
                                                                        <span className="text-xs mt-1" style={{ color: palette.primary || '#3b82f6' }}>
                                                                            {address.geoData.distanceKm ? `${address.geoData.distanceKm} km` : ''}
                                                                            {address.geoData.distanceKm && address.geoData.distanceTime ? ' • ' : ''}
                                                                            {address.geoData.distanceTime ? `${address.geoData.distanceTime} min` : ''}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>

                        {/* Note */}
                        <div className="input-box">
                            <label htmlFor="notres">Note</label>
                            <textarea name="notes" id="notes" onChange={handleSystemDataInput} value={systemDataInput.notes}></textarea>
                        </div>

                    </div>
                    {/* TIPOLOGIA IMPIANTO */}
                    <div className="w-full mt-3 mb-1">
                        <div className="flex justify-between items-center mb-1">
                            <h2 className="text-lg font-medium mb-0" style={{ color: palette.titleColor || '#111827' }}>Elenco tipologie impianto</h2>
                            <button 
                                className="flex items-center text-sm px-2 py-1 rounded transition-all" 
                                style={{
                                    backgroundColor: palette.buttonPrimaryBg || palette.primary || '#0078d4',
                                    color: palette.buttonPrimaryText || palette.textOnPrimary || '#ffffff',
                                    border: `1px solid ${palette.buttons?.primary?.border || 'transparent'}`,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                                onClick={() => {
                                    if (systemServicePoolRef && systemServicePoolRef.current) {
                                        systemServicePoolRef.current.setIsAdding(true);
                                    }
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = palette.buttonPrimaryHover || palette.primaryDark || '#006dbf';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = palette.buttonPrimaryBg || palette.primary || '#0078d4';
                                }}
                            >
                                <DynamicIcon iconClass="fa-solid fa-plus mr-1" style={{ color: palette.buttonPrimaryText || palette.textOnPrimary || '#ffffff' }} />
                                Tipo impianto
                            </button>
                        </div>
                        <div className="w-full mt-1">
                            {/* Passa il riferimento al componente per accedere direttamente alle sue funzioni */}
                            <SystemServicePool 
                                ref={systemServicePoolRef}
                                arrayData={systemDataInput?.typesList} 
                                addEditService={addEditItem} 
                                deleteService={deleteItem} 
                            />
                        </div>
                    </div>

                    {/* Alert di validazione */}
                    {showValidation && validationErrors.length > 0 && (
                        <div className="mb-4">
                            <Alert
                                type="error"
                                dismissible={true}
                                onClose={() => setShowValidation(false)}
                                message={
                                    <div className="flex items-start">
                                        <div className="mr-2">
                                            <DynamicIcon iconClass="fas fa-exclamation-triangle" />
                                        </div>
                                        <div>
                                            <div className="font-semibold mb-1">Impossibile salvare l'impianto</div>
                                            <ul className="list-disc list-inside">
                                                {validationErrors.map((error, index) => (
                                                    <li key={index}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                }
                            />
                        </div>
                    )}

                    <div className="flex gap-2 h-[40px]">
                        {
                            (sendFetchStatus === 'i' || sendFetchStatus === 's') &&
                            <div className="flex gap-2">
                                {
                                    sendFetchStatus === 's' ? (
                                        <h3>Impianto {systemId ? 'aggiornato' : 'creato'} con successo.</h3>
                                    ) : (
                                        <button 
                                            className="primary flex items-center gap-2 px-4 py-2 rounded" 
                                            onClick={sendSystemData}
                                            style={{
                                                backgroundColor: palette.buttonPrimaryBg || palette.primary,
                                                color: palette.buttonPrimaryText || palette.textOnPrimary,
                                                border: `1px solid ${palette.buttons?.primary?.border || 'transparent'}`
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = palette.buttonPrimaryHover || palette.primary;
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor = palette.buttonPrimaryBg || palette.primary;
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-save mr-1" style={{ color: palette.buttonPrimaryText || palette.textOnPrimary }} />
                                            {systemId ? 'Salva modifiche' : 'Crea nuovo impianto'}
                                        </button>
                                    )
                                }
                                {
                                    (
                                        rightModalOldState.oldMode &&
                                        rightModalOldState.oldId &&
                                        rightModalOldState.oldPercentuage) ? (
                                        <button
                                            onClick={() => {
                                                dispatch(setSwap({
                                                    id: rightModalOldState.oldId,
                                                    mode: rightModalOldState.oldMode,
                                                    percentuageWidth: rightModalOldState.oldPercentuage,
                                                    isOld: false
                                                }))
                                            }}
                                            className="secondary flex items-center gap-2 px-4 py-2 rounded"
                                            style={{
                                                backgroundColor: palette.buttonSecondaryBg || '#6c757d',
                                                color: palette.buttonSecondaryText || '#ffffff',
                                                border: `1px solid ${palette.buttons?.secondary?.border || 'transparent'}`
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = palette.buttonSecondaryHover || '#5a6268';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor = palette.buttonSecondaryBg || '#6c757d';
                                            }}
                                        >Torna indietro</button>
                                    ) : (
                                        <button 
                                            className="secondary flex items-center gap-2 px-4 py-2 rounded" 
                                            onClick={() => dispatch(setGoingToClose())}
                                            style={{
                                                backgroundColor: palette.buttonSecondaryBg || '#6c757d',
                                                color: palette.buttonSecondaryText || '#ffffff',
                                                border: `1px solid ${palette.buttons?.secondary?.border || 'transparent'}`
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = palette.buttonSecondaryHover || '#5a6268';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor = palette.buttonSecondaryBg || '#6c757d';
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-times mr-1" style={{ color: palette.buttonSecondaryText || '#ffffff' }} />
                                            Chiudi
                                        </button>
                                    )
                                }
                            </div>
                        }

                        {
                            sendFetchStatus === 'l' &&
                            <Loader />
                        }
                        {
                            sendFetchStatus === 'f' &&
                            <Error message={sendError} />
                        }
                    </div>
                </div>
            }
        </div >
    )
}
